import React, { useEffect, useState } from "react";
import { TrendingUp, ArrowRight, ArrowDown, Award, Users, X, Check, ShieldCheck, BookOpen, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Logo } from "./Shared";
import { SOCIAL_PROOF, PAIN_SOLUTIONS } from "../mock";
import { track } from "../api";

export default function Landing({ onStart }) {
  const [storyOpen, setStoryOpen] = useState(false);
  const [storyTab, setStoryTab] = useState("bnb");
  useEffect(() => {
    track("landing_view");
  }, []);
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-white/90 px-4 py-3 backdrop-blur">
        <Logo className="h-8" />
        <span className="hidden text-xs text-muted-foreground sm:block">
          合作場域資格問卷
        </span>
      </header>

      {/* HERO — 第一眼：簡單、舒服、大字 */}
      <section className="hero-gradient">
        <div className="mx-auto max-w-2xl px-6 pb-16 pt-14 text-center sm:pb-24 sm:pt-24">
          <div className="fade-up">
            <p className="mb-5 inline-block rounded-full bg-white px-4 py-1.5 text-sm font-medium text-brand shadow-sm">
              每月僅限 3 個新場域 · 2 分鐘完成
            </p>
            <h1 className="text-[52px] font-black leading-[1.05] text-brand sm:text-7xl">
              空房就是燒錢
            </h1>
            <p className="mx-auto mt-6 max-w-md text-xl font-medium leading-relaxed text-foreground sm:text-2xl">
              你的場域，是在幫你賺錢，還是讓你操心？
            </p>
            <p className="mx-auto mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm font-bold text-brand">
              <span className="rounded-full bg-white px-3 py-1 shadow-sm">包棟民宿</span>
              <span className="rounded-full bg-white px-3 py-1 shadow-sm">豪華露營區</span>
            </p>

            {/* CTA */}
            <Button
              onClick={onStart}
              className="mt-9 h-14 w-full max-w-sm bg-brand text-lg font-bold text-white shadow-lg shadow-orange-200 hover:bg-[#EA580C]"
            >
              填寫合作資格問卷
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* 痛點 — 先呈現，有溫度 */}
      <section className="mx-auto max-w-2xl px-6 pt-16">
        <div className="text-center">
          <p className="mb-3 inline-block rounded-full bg-orange-100 px-4 py-1 text-sm font-bold text-brand">
            場主的真實困境
          </p>
          <h2 className="text-[26px] font-black leading-snug sm:text-3xl">
            你是不是也<span className="text-brand">卡在這些狀況</span>？
          </h2>
          <p className="mx-auto mt-4 max-w-md text-lg leading-relaxed text-muted-foreground">
            房間、營位就在那裡，客人卻進不來。<br className="hidden sm:block" />
            每一個空房、空著的帳篷，都是<span className="font-bold text-foreground">白白燒掉的成本</span>。
          </p>
        </div>

        <div className="mt-8 space-y-3.5">
          {PAIN_SOLUTIONS.map((item, i) => (
            <div
              key={item.pain}
              className="fade-up flex items-center gap-4 rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-brand shadow-sm">
                <X className="h-5 w-5" />
              </span>
              <p className="text-lg font-bold text-[#9A3412]">{item.pain}</p>
            </div>
          ))}
        </div>

        {/* 可摺疊的小故事 */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setStoryOpen((v) => !v)}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-orange-50"
            data-testid="pain-story-toggle"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-orange-100 text-brand">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="flex-1">
              <span className="block text-base font-bold">一位場主的真實故事</span>
              <span className="block text-sm text-muted-foreground">點開看看，你是不是也有一樣的心情</span>
            </span>
            <ChevronDown
              className={`h-5 w-5 shrink-0 text-brand transition-transform duration-300 ${storyOpen ? "rotate-180" : ""}`}
            />
          </button>
          {storyOpen && (
            <div className="fade-up border-t border-border" data-testid="pain-story-content">
              {/* 切換：民宿 / 露營區主人 */}
              <div className="flex gap-2 px-5 pt-4">
                <button
                  type="button"
                  onClick={() => setStoryTab("bnb")}
                  data-testid="story-tab-bnb"
                  className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition ${
                    storyTab === "bnb" ? "bg-brand text-white" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  民宿主人
                </button>
                <button
                  type="button"
                  onClick={() => setStoryTab("camp")}
                  data-testid="story-tab-camp"
                  className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition ${
                    storyTab === "camp" ? "bg-brand text-white" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  露營區主人
                </button>
              </div>

              <p className="px-5 pt-3 text-xs text-muted-foreground">※ 以下為真實案例改寫，姓名皆為化名。</p>

              {storyTab === "bnb" ? (
                <div className="space-y-4 px-5 py-5 text-[15px] leading-loose text-foreground">
                  <p><span className="font-bold text-[#9A3412]">阿哲｜38 歲</span>，原本在台北做室內設計。三年前帶著多年積蓄回到宜蘭，頂下一間四房的包棟民宿，只想給家人一個離自然近一點的生活。</p>
                  <p>剛開始滿懷期待，假日偶爾客滿，但平日的房間，總是靜靜地空著。</p>
                  <p>他試過自己投廣告、找網紅，錢花了、成效卻看不太到；訊息一則一則進來，他認真回覆，客人卻常常回一回就沒了下文。</p>
                  <p>他也想過請一個懂行銷的人來幫忙，可是淡季的營收，連自己都快撐不住，哪來的預算養一位全職人才？</p>
                  <p>於是他一個人，白天顧現場、晚上回訊息、半夜還在想行銷，蠟燭兩頭燒。</p>
                  <p className="font-bold text-[#9A3412]">看著一間又一間的空房，他終於明白——每一個沒賣出去的夜晚，都是白白燒掉的錢。</p>
                  <p className="text-sm text-muted-foreground">如果這也是你的故事，往下看看，人客多能怎麼幫你。</p>
                </div>
              ) : (
                <div className="space-y-4 px-5 py-5 text-[15px] leading-loose text-foreground">
                  <p><span className="font-bold text-[#9A3412]">小柔｜35 歲</span>，離開忙碌的餐飲業後，兩年前在南投山上，親手打造了一區六帳的豪華露營，只想把最好的風景分享給旅人。</p>
                  <p>帳篷裡什麼都有，美得像明信片。假日一位難求，可是一到平日，一頂頂空著的帳篷就這樣曬著太陽。</p>
                  <p>她也投過廣告、找過網紅，錢花了、人潮卻沒跟著來；客人傳訊來問，她一邊忙場地一邊回，常常回一回就沒了下文。</p>
                  <p>她想找個懂行銷的人幫忙，可是淡季的收入，光是維護營區就快見底，哪有多的預算養人？</p>
                  <p>於是她一個人，白天顧營區、晚上回訊息、深夜還在研究怎麼曝光。</p>
                  <p className="font-bold text-[#9A3412]">看著一頂頂空下來的帳篷，她也明白了——每一個沒訂出去的營位，都是白白流走的收入。</p>
                  <p className="text-sm text-muted-foreground">如果這也是你的故事，往下看看，人客多能怎麼幫你。</p>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="mt-8 flex items-center justify-center gap-2 text-center text-lg font-bold">
          這些，人客多都能幫你解決
          <ArrowDown className="h-5 w-5 text-brand" />
        </p>
      </section>

      {/* 解決方案 — 後呈現 */}
      <section className="mx-auto max-w-2xl px-6 pt-12">
        <div className="text-center">
          <p className="mb-3 inline-block rounded-full bg-orange-100 px-4 py-1 text-sm font-bold text-brand">
            人客多的解法
          </p>
          <h2 className="text-[26px] font-black leading-snug sm:text-3xl">
            這些，我們<span className="text-brand">一次幫你解決</span>
          </h2>
        </div>

        <div className="mt-8 space-y-4">
          {PAIN_SOLUTIONS.map((item, i) => (
            <div
              key={item.solve}
              className="fade-up flex items-start gap-3 rounded-2xl border border-border bg-white p-5 shadow-sm transition hover:shadow-md"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand text-white">
                <Check className="h-5 w-5" />
              </span>
              <div>
                <p className="text-lg font-black text-[#9A3412]">{item.solve}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.feature}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 整體解決方案 — 量化承諾 */}
      <section className="mx-auto max-w-2xl px-6 pt-14 text-center">
        <h2 className="text-[24px] font-black leading-snug sm:text-3xl">
          人客多整體解決方案
        </h2>
        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
          潛在客戶引流、KOL 曝光與合作、Google SEO、11 小時待命客服，全交給我們。
          你只要專注現場管理，每個月為你的場域帶來：
        </p>
        <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-4">
          <div className="rounded-2xl border border-orange-200 bg-orange-50 px-2 py-4 sm:p-4 text-center">
            <Users className="mx-auto mb-1.5 h-6 w-6 text-brand" />
            <p className="whitespace-nowrap text-lg font-black leading-none text-brand sm:text-3xl">400–1500</p>
            <p className="mt-1.5 text-[11px] font-bold leading-tight sm:text-sm">精準潛在客戶名單</p>
          </div>
          <div className="rounded-2xl border border-orange-200 bg-orange-50 px-2 py-4 sm:p-4 text-center">
            <TrendingUp className="mx-auto mb-1.5 h-6 w-6 text-brand" />
            <p className="whitespace-nowrap text-lg font-black leading-none text-brand sm:text-3xl">80%</p>
            <p className="mt-1.5 text-[11px] font-bold leading-tight sm:text-sm">住房率 8 成</p>
          </div>
          <div className="rounded-2xl border border-orange-200 bg-orange-50 px-2 py-4 sm:p-4 text-center">
            <Award className="mx-auto mb-1.5 h-6 w-6 text-brand" />
            <p className="whitespace-nowrap text-lg font-black leading-none text-brand sm:text-3xl">4–8 位</p>
            <p className="mt-1.5 text-[11px] font-bold leading-tight sm:text-sm">保障網紅合作</p>
          </div>
        </div>
      </section>

      {/* 全台唯一 — 人客多特色，重點強調 */}
      <section className="mx-auto max-w-2xl px-6 pt-14">
        <div className="overflow-hidden rounded-3xl border-2 border-brand bg-white p-5 text-center shadow-lg sm:p-7">
          <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-1.5 text-sm font-bold text-white">
            <ShieldCheck className="h-4 w-4" /> 全台唯一
          </span>
          <p className="text-lg font-black leading-relaxed text-foreground sm:text-2xl">
            擁有<span className="text-brand">自己的 PMS 系統</span>，
            親手從 0 打造<span className="text-brand">住房率 9 成</span>的奢華露營，
            創造<span className="text-brand">最多網紅入住民宿</span>的
            <span className="text-brand">全代管團隊</span>
          </p>
          <div className="mt-5 h-px bg-orange-100" />
          <div className="mt-5">
            <p className="text-base font-bold text-[#9A3412]">合作後平均營收提升</p>
            <p className="my-1 whitespace-nowrap text-5xl font-black leading-none text-brand sm:text-7xl">2 倍 – 4.8 倍</p>
          </div>
        </div>

        <div className="mx-auto mt-6 flex max-w-md items-center justify-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3.5 text-base font-medium text-[#9A3412]">
          <TrendingUp className="h-5 w-5 shrink-0 text-brand" />
          {SOCIAL_PROOF}
        </div>
      </section>

      {/* 底部 CTA */}
      <section className="brand-gradient">
        <div className="mx-auto max-w-3xl px-6 py-14 text-center">
          <h2 className="text-3xl font-black sm:text-4xl">
            現在就評估你的<span className="text-brand">合作資格</span>
          </h2>
          <p className="mx-auto mt-3 max-w-md text-base text-muted-foreground sm:text-lg">
            2 分鐘完成，立即知道你的場域是否符合合作條件
          </p>
          <Button
            onClick={onStart}
            className="mt-7 h-14 w-full max-w-sm bg-brand text-lg font-bold text-white hover:bg-[#EA580C]"
          >
            填寫合作資格問卷
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} 人客多 Lakeze 旅宿集團
      </footer>
    </div>
  );
}
